import React from "react";
import { Truck, Info } from "lucide-react";

/**
 * Shipping & pick-up options block. Reads `listing.shipping_options` —
 * the backend contract is `[{label, price}]` (label = destination/desc
 * combined, price = number in NZD; 0 renders as "Free"). Falls back to
 * a single "Pick up from <area>" row when the seller hasn't defined any
 * custom options.
 */
export default function ShippingOptions({ listing }) {
  const raw = Array.isArray(listing.shipping_options) ? listing.shipping_options : [];
  const rows = raw.length
    ? raw
    : listing.city || listing.area
    ? [{ label: `Pick up from ${listing.area || listing.city}`, price: 0 }]
    : [];
  if (!rows.length) return null;
  return (
    <section
      data-testid="ad-shipping-options"
      className="mt-6 pt-6 border-t border-subtle"
    >
      <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-4">
        <h3 className="text-sm font-semibold text-slate-600 flex items-center gap-2">
          <Truck className="w-4 h-4 text-[color:var(--brand-blue)]" />
          Shipping &amp; pick-up options
        </h3>
        <div>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left text-xs font-semibold text-slate-500 border-b border-soft">
                <th className="py-2 pr-4">Destination &amp; description</th>
                <th className="py-2 text-right">Price</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const price = Number(r.price);
                const priceLabel = !price || price === 0 ? "Free" : `$${price.toFixed(2)}`;
                return (
                  <tr key={i} className="border-b border-subtle last:border-0">
                    <td className="py-3 pr-4 text-slate-700">{r.label}</td>
                    <td
                      className="py-3 text-right font-semibold text-slate-800"
                      data-testid={`ad-shipping-price-${i}`}
                    >
                      {priceLabel}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <a
            href="/policies/shipping"
            data-testid="ad-shipping-learn-more"
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--brand-blue)] hover:underline border-l-2 border-[color:var(--brand-blue)] pl-3 py-1.5 bg-[color:var(--brand-blue-wash)]/40 rounded-r"
          >
            <Info className="w-3.5 h-3.5" /> Learn more about shipping &amp; delivery options.
          </a>
        </div>
      </div>
    </section>
  );
}
